import { useRef, useState, type ReactElement } from 'react';
import classnames from 'classnames';
import { MediaControls, type CommonControlsProps } from 'dibs-elements/exports/MediaControls.tsx';
import { IS_IOS_WEBKIT } from 'dibs-elements/exports/useFullscreen.tsx';
import { NftModelViewerLazy } from './NftModelViewerLazy.tsx';
import styles from './NftModelViewer.scss';

import { useLazyLoadMedia } from 'dibs-elements/exports/useLazyLoadMedia.tsx';

type Props = {
    handleZoom?: () => void;
    src: string;
    posterFrameUrl?: string | null;
    height?: string;
    width?: string;
    constainerClass?: string;
    onFullScreenOpen?: () => void;
    onFullScreenClose?: () => void;
};

export const NftModelViewer = <T extends string = '', U extends string = ''>({
    src,
    posterFrameUrl,
    height,
    width,
    constainerClass,
    onFullScreenOpen,
    onFullScreenClose,
    ...controlProps
}: Props & CommonControlsProps<T, U>): ReactElement => {
    const containerRef = useRef<HTMLElement>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const lazyLoad = useLazyLoadMedia('3d', {
        width,
        height,
    });

    return (
        <MediaControls
            {...controlProps}
            mediaRef={containerRef}
            className={classnames(styles.wrapper, constainerClass, {
                [styles.fullscreen]: isFullScreen,
            })}
            onFullScreenOpen={() => {
                setIsFullScreen(true);
                onFullScreenOpen?.();
            }}
            onFullScreenClose={() => {
                setIsFullScreen(false);
                onFullScreenClose?.();
            }}
            controls={IS_IOS_WEBKIT ? 'never' : controlProps.controls}
        >
            {/* lazy load placeholder */}
            <div
                style={lazyLoad.styleProps.style}
                className={classnames(lazyLoad.styleProps.className, {
                    [styles.fullscreen]: isFullScreen,
                })}
            >
                <NftModelViewerLazy
                    posterSrc={posterFrameUrl}
                    src={src}
                    style={{
                        width: '100%',
                        height: '100%',
                        maxHeight: '100vh',
                        maxWidth: '100vw',
                    }}
                    onLoad={lazyLoad.handlerProps.onLoad}
                />
            </div>
        </MediaControls>
    );
};
